// authStore.js

import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import * as Sentry from "@sentry/vue";
import {
  getAuth,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  deleteUser,
  // inMemoryPersistence,
  // browserSessionPersistence,
} from "firebase/auth";
// import axios from "axios";
import { fetchWithAuth } from "@/api/fetchApi";
import { postAuthWithToken } from "@/api/authApi";
import {
  GoogleAuthProvider,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { sendPasswordResetEmail } from "firebase/auth";
import router from "@/router";
import { useRoomStore } from "./roomStore";

export const useAuthStore = defineStore("auth", () => {
  const user = ref(null);
  watch(user, (newUser) => {
    if (newUser?.uid) {
      Sentry.setUser({ id: newUser.uid });
    } else {
      Sentry.setUser(null);
    }
  });
  const userEmail = computed(() => user.value?.email);
  const idToken = ref(null);
  const isAuthenticated = ref(false);
  const isGuest = computed(() => {
    return user.value?.email?.startsWith("guest@") ?? false;
  });
  const initialized = ref(false);
  const roomStore = useRoomStore();

  // ✅ ユーザー設定
  const userSettings = ref({
    user_type: 'normal',         // ユーザー種別（normal/taitockeye）
    allow_public_share: false,
    allowed_room_types: [],      // 空配列 = すべて許可
    allowed_room_settings: [],   // 空配列 = すべて許可
    can_access_text2image: false, // Text to Image機能へのアクセス権限
    can_access_text2model: false, // Text to Model機能へのアクセス権限
    can_access_image2model: false, // Image to Model機能へのアクセス権限
    can_access_text2video: false, // 動画生成（テキスト→動画・画像→動画）へのアクセス権限
    can_access_dig: false,        // 防災マップ（DIG）機能へのアクセス権限
    can_access_skysea: false,     // 船舶・航空機マップ機能へのアクセス権限
    can_access_chat: false,       // チャット機能へのアクセス権限
    can_access_ai_npc: false,     // AI NPC機能へのアクセス権限
    can_access_operation: false,  // オペレーション画面へのアクセス権限
    can_access_spaces: false,     // スペース管理画面（物理スペース + Garden）へのアクセス権限
    can_access_okaki: false,      // Okaki（絵を動かす）機能へのアクセス権限
    can_access_slide_video: false, // スライド動画（視線課題動画の作成）機能へのアクセス権限
    can_access_bizhub: false,     // 業務管理（bizhub）へのアクセス権限
    can_multiplay: true,          // マルチプレイ機能の権限（デフォルトはオン）
    can_capture: true,            // キャプチャ機能の権限（デフォルトはオン）
    can_use_space: true,          // スペース機能の権限（デフォルトはオン）
    operation_template_enabled: false, // オペレーション画面テンプレートが有効か
    _loaded: false,              // 設定がAPIからロードされたかどうか
  });

  // ✅ マニュアル系ユーザーかどうか（taitockeye または qumowill）
  const isManualUser = computed(() => {
    return ['taitockeye', 'qumowill'].includes(userSettings.value.user_type);
  });

  // ✅ ユーザー種別に応じたホーム画面パス
  const homePath = computed(() => {
    return isManualUser.value ? '/app/manual' : '/app/rooms';
  });

  const canAccessAnyGeneration = computed(
    () =>
      !!(
        userSettings.value.can_access_text2image ||
        userSettings.value.can_access_text2video ||
        userSettings.value.can_access_text2model ||
        userSettings.value.can_access_image2model
      )
  );

  const signup = async (email, password, userType = "normal") => {
    try {
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      console.log("✅ Firebase ユーザー登録成功:", userCredential);
      user.value = await postAuthWithToken(userCredential.user, "/register/", null, userType);

      console.log("✅ Django ユーザー登録成功 (user_type:", userType, ")");
    } catch (error) {
      let message = "サインアップに失敗しました";

      switch (error.code) {
        case "auth/email-already-in-use":
          message = "このメールアドレスはすでに登録されています";
          break;
        case "auth/invalid-email":
          message = "メールアドレスの形式が正しくありません";
          break;
        case "auth/weak-password":
          message = "パスワードは6文字以上にしてください";
          break;
        case "auth/missing-password":
          message = "パスワードを入力してください";
          break;
        case "auth/operation-not-allowed":
          message =
            "この操作は許可されていません（管理者にお問い合わせください）";
          break;
        default:
          message = "エラーが発生しました：" + error.message;
          break;
      }

      alert(message); // v-alert や toast に置き換え可能
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      user.value = await postAuthWithToken(userCredential.user);
      console.log("✅ ログイン成功");
    } catch (error) {
      let message = "ログインに失敗しました";

      switch (error.code) {
        case "auth/user-not-found":
        case "auth/invalid-credential":
          message =
            "ログインできません。メールアドレスとパスワードをご確認ください";
          break;
        case "auth/wrong-password":
          message = "パスワードが間違っています";
          break;
        case "auth/invalid-email":
          message = "メールアドレスの形式が正しくありません";
          break;
        case "auth/too-many-requests":
          message =
            "ログイン試行が多すぎます。しばらくしてから再試行してください";
          break;
        case "auth/missing-password":
          message = "パスワードを入力してください";
          break;
        case "auth/internal-error":
          message = "内部エラーが発生しました。もう一度お試しください";
          break;
        default:
          message = "エラーが発生しました：" + error.message;
          break;
      }

      // 表示方法はお好みで：alert, toast, UIメッセージなど
      alert(message);
      throw error;
    }
  };

  // Google認証: リダイレクト方式（ポップアップを使わない）
  const loginWithGoogle = async (userType = null) => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    // リダイレクト後にuserTypeを復元するためlocalStorageに保存
    if (userType) {
      localStorage.setItem("googleAuthUserType", userType);
    } else {
      localStorage.removeItem("googleAuthUserType");
    }

    console.log("[Google] Login started (redirect method)...");
    await signInWithRedirect(auth, provider);
    // ここには戻らない（Googleページにリダイレクトされる）
  };

  // Google認証リダイレクト後の結果処理（LoginPageのonMountedで呼ぶ）
  const handleGoogleRedirectResult = async () => {
    const auth = getAuth();
    try {
      const result = await getRedirectResult(auth);
      if (!result) {
        return false; // リダイレクト結果なし（通常のページ読み込み）
      }

      console.log("[Google] Redirect result received:", result.user.email);
      const firebaseUser = result.user;
      const token = await firebaseUser.getIdToken(true);
      console.log("[Google] Got ID token");

      const displayName = firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "";
      const userType = localStorage.getItem("googleAuthUserType") || null;
      localStorage.removeItem("googleAuthUserType");

      console.log("[Google] Sending to Django...", displayName, "userType:", userType);
      user.value = await postAuthWithToken(firebaseUser, "/login/", displayName, userType);
      idToken.value = token;
      isAuthenticated.value = true;
      console.log("[Google] Login successful!");

      return true;
    } catch (error) {
      console.error("[Google] Redirect result error:", error);
      return false;
    }
  };

  // ✅ ユーザー種別に応じたランディングページパス
  const landingPath = computed(() => {
    const ut = userSettings.value.user_type;
    if (ut === 'taitockeye') return '/taitock';
    if (ut === 'qumowill') return '/qumowill';
    return '/';
  });

  const logout = async () => {
    try {
      const logoutPath = landingPath.value;
      const auth = getAuth();
      await signOut(auth);
      user.value = null;
      // idToken.value = null;
      isAuthenticated.value = false;
      localStorage.removeItem("brand_user_type");
      localStorage.removeItem("poranos_admin_hint");
      localStorage.removeItem("poranos_session");
      console.log("✅ ログアウト成功");
      router.push(logoutPath);
    } catch (error) {
      console.error("❌ ログアウト失敗:", error.message);
    }
  };

  const sendPasswordReset = async (email) => {
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      alert(`${email} あてにパスワード再設定用のメールを送信しました`);
    } catch (error) {
      console.error("❌ パスワードリセット失敗:", error);
      alert("パスワードリセットに失敗しました: " + error.message);
    }
  };

  const loginAsGuest = async () => {
    const firebaseAuth = getAuth();
    const { getApiBaseUrl } = await import("@/api/fetchApi");
    const API_BASE = getApiBaseUrl();

    try {
      // バックエンドからゲスト用 Custom Token を取得
      const res = await fetch(`${API_BASE}/guest-token/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { custom_token } = await res.json();

      // Custom Token で Firebase 認証
      const result = await signInWithCustomToken(firebaseAuth, custom_token);
      const firebaseUser = result.user;
      user.value = firebaseUser;
      idToken.value = await firebaseUser.getIdToken(true);
      isAuthenticated.value = true;
      roomStore.loadRooms();
    } catch (error) {
      console.error("Guest login failed:", error.message);
    }
  };

  const setUser = (firebaseUser) => {
    user.value = firebaseUser;
    isAuthenticated.value = true;
  };

  // 同時に走った loadUserSettings を 1 本の実リクエストに束ねるための in-flight promise。
  // router のナビゲーションガードは複数箇所から loadUserSettings を呼ぶため、
  // ルーム詳細を開くだけで /account-detail/（約 200KB）が 2 本出ていた。
  let userSettingsInFlight = null;

  // ✅ ユーザー設定を取得
  const loadUserSettings = async () => {
    if (userSettingsInFlight) return userSettingsInFlight;
    userSettingsInFlight = _loadUserSettings().finally(() => {
      userSettingsInFlight = null;
    });
    return userSettingsInFlight;
  };

  const _loadUserSettings = async () => {
    try {
      const data = await fetchWithAuth("/account-detail/");
      console.log("[loadUserSettings] API response:", data);
      if (data && data.user) {
        userSettings.value = {
          user_type: data.user.user_type || 'normal',
          allow_public_share: data.user.allow_public_share || false,
          allowed_room_types: data.user.allowed_room_types || [],
          allowed_room_settings: data.user.allowed_room_settings || [],
          can_access_text2image: data.user.can_access_text2image || false,
          can_access_text2model: data.user.can_access_text2model || false,
          can_access_image2model: data.user.can_access_image2model || false,
          can_access_text2video: data.user.can_access_text2video || false,
          can_access_dig: data.user.can_access_dig || false,
          can_access_skysea: data.user.can_access_skysea || false,
          can_access_chat: data.user.can_access_chat || false,
          can_access_ai_npc: data.user.can_access_ai_npc || false,
          can_access_operation: data.user.can_access_operation || false,
          can_access_spaces: data.user.can_access_spaces || false,
          can_access_okaki: data.user.can_access_okaki || false,
          can_access_slide_video: data.user.can_access_slide_video || false,
          can_access_bizhub: data.user.can_access_bizhub || false,
          can_multiplay: data.user.can_multiplay !== undefined ? data.user.can_multiplay : true,
          can_capture: data.user.can_capture !== undefined ? data.user.can_capture : true,
          can_use_space: data.user.can_use_space !== undefined ? data.user.can_use_space : true,
          is_admin: data.user.is_admin || false,
          operation_template_enabled: data.operation_template?.is_enabled || false,
          _loaded: true,
        };
        console.log("[loadUserSettings] userSettings updated:", userSettings.value);
        console.log("[loadUserSettings] homePath is now:", ['taitockeye', 'qumowill'].includes(userSettings.value.user_type) ? '/app/manual' : '/app/rooms');

        // ブランド即時切り替え用にuser_typeをlocalStorageに保存（UI表示のみ、認証には使用しない）
        localStorage.setItem("brand_user_type", userSettings.value.user_type);

        // 公開サイト（VitePress）のナビ表示切替用（ログイン中は「ログイン」を「アプリを開く」に）。認証の実体は Firebase
        localStorage.setItem("poranos_session", "1");
        // 公開サイト（VitePress）の管理者向けリンク帯の表示切替用。権限の実体は API 側
        if (userSettings.value.is_admin) {
          localStorage.setItem("poranos_admin_hint", "1");
        } else {
          localStorage.removeItem("poranos_admin_hint");
        }

        // Qumowillユーザーの初回のみライトモードをデフォルトにする
        if (userSettings.value.user_type === 'qumowill' && !localStorage.getItem("qumowill_theme_initialized")) {
          localStorage.setItem("qumowill_theme_initialized", "true");
          if (localStorage.getItem("darkMode") === null || localStorage.getItem("darkMode") === "true") {
            const { useThemeStore } = await import("@/stores/themeStore");
            const themeStore = useThemeStore();
            if (themeStore.isDarkMode) {
              themeStore.toggleTheme();
            }
          }
        }
      }
    } catch (err) {
      console.error("ユーザー設定の取得に失敗:", err);
    }
  };

  const initAuth = () => {
    return new Promise(async (resolve) => {
      const auth = getAuth();

      // Clear any stale google auth flags
      localStorage.removeItem("googleAuthInProgress");

      // 初回のみ処理し、リスナーを解除する（loginWithGoogle等との競合を防ぐ）
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        unsubscribe();
        if (firebaseUser) {
          user.value = firebaseUser;
          isAuthenticated.value = true;
          // ✅ ログイン時にユーザー設定を取得
          await loadUserSettings();
        } else {
          // 未ログイン状態 — ゲストログインは「デモを試す」ボタン経由のみ
          user.value = null;
          isAuthenticated.value = false;
        }
        initialized.value = true;
        resolve();
      });
    });
  };

  // const initializeAuth = () => {
  //   const auth = getAuth();
  //   onAuthStateChanged(auth, async (firebaseUser) => {
  //     if (firebaseUser) {
  //       user.value = firebaseUser;
  //       idToken.value = await firebaseUser.getIdToken(true);
  //       localStorage.setItem("idToken", idToken.value);
  //       isAuthenticated.value = true;
  //       console.log("✅ Firebase 認証状態確認: ユーザーがログイン中");
  //     } else {
  //       // ゲストとして扱う
  //       localStorage.removeItem("idToken");
  //       user.value = { uid: "guest", email: null };
  //       idToken.value = null;
  //       isAuthenticated.value = true;
  //       console.log("👤 ゲストユーザーとしてログイン中");
  //     }
  //   });
  // };

  const deleteAccount = async () => {
    const auth = getAuth();
    const firebaseUser = auth.currentUser;

    if (!firebaseUser) {
      alert("ログインユーザーが見つかりません");
      return;
    }

    try {
      // ① Django側のアカウント削除APIを呼び出し（Firebase削除も含む）
      await fetchWithAuth("/delete-account/", {
        method: "DELETE",
      });
      console.log("✅ Django側アカウント削除完了（Firebase含む）");

      // ② Firebaseのアカウントも削除（バックアップ、既に削除済みの場合はエラーを無視）
      try {
        await deleteUser(firebaseUser);
        console.log("✅ Firebaseアカウント削除完了");
      } catch (firebaseError) {
        // バックエンドで既に削除されている場合は無視
        console.log("ℹ️ Firebaseアカウントは既に削除済み:", firebaseError.code);
      }

      // 認証状態をクリアし、ログアウト
      await logout();
    } catch (error) {
      console.error("❌ アカウント削除失敗:", error);
      alert("アカウント削除に失敗しました。再ログインが必要かもしれません。");
    }
  };

  const isLoginModalOpen = ref(false);
  const isSignupMode = ref(false);

  const loginStep = ref("login"); // 'login', 'signup', 'emailLogin', 'emailSignup'

  const openLoginModal = () => {
    // ログインページに遷移（COEPの制約を回避するため）
    // フルページリロードで新しいヘッダーを適用させる
    const currentPath = window.location.pathname;
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
  };

  const closeLoginModal = () => {
    isLoginModalOpen.value = false;
    loginStep.value = "login"; // 初期化
  };

  const goToSignup = () => {
    loginStep.value = "signup";
  };

  const goToEmailLogin = () => {
    loginStep.value = "emailLogin";
  };

  const goToEmailSignup = () => {
    loginStep.value = "emailSignup";
  };

  const backToLogin = () => {
    loginStep.value = "login";
  };

  const isOwnedByCurrentUser = (file) => {
    return user.value?.email === file.owner_email;
  };
  const isOwnedByEmail = (email) => {
    return user.value?.email === email;
  };

  return {
    user,
    isAuthenticated,
    signup,
    login,
    logout,
    // initializeAuth,
    isOwnedByCurrentUser,
    isOwnedByEmail,
    postAuthWithToken,
    loginWithGoogle,
    handleGoogleRedirectResult,
    isGuest,
    sendPasswordReset,
    loginAsGuest,
    deleteAccount,
    setUser,
    initAuth,
    userEmail,
    userSettings,
    loadUserSettings,
    homePath,
    canAccessAnyGeneration,
    isManualUser,
    landingPath,

    isLoginModalOpen,
    isSignupMode,
    loginStep,
    openLoginModal,
    closeLoginModal,
    goToSignup,
    goToEmailLogin,
    goToEmailSignup,
    backToLogin,
  };
});

export async function fetchAllUsers() {
  return await fetchWithAuth("/users/", {
    method: "GET",
  });
}
